import { Box, Fade, Tooltip } from '@mui/material';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { LegacyMuiButton } from '~/legacy-components/LegacyForms';

import { EquipListType } from './data';
import { newEquipmentData } from './data/newEquipment';
import { IFormInput, RowData } from './useEquipmentTab';
import { useSxStyles } from './useStyles';

export const ActionButtons = ({
  setEditMode,
  setEditedRows,
  setDeletedRows,
  useFormMethods,
  onSubmit,
  defaultData,
  useFieldArrayMethods,
  invNumber,
  updateInvNumber,
  equipList,
  editMode,
}: {
  setEditMode: Dispatch<SetStateAction<boolean>>;
  onSubmit: () => void;
  setEditedRows: Dispatch<SetStateAction<number[]>>;
  setDeletedRows: Dispatch<SetStateAction<number[]>>;
  useFormMethods: UseFormReturn<IFormInput>;
  defaultData: RowData[];
  useFieldArrayMethods: UseFieldArrayReturn<IFormInput, 'equipment', 'id'>;
  invNumber: string;
  updateInvNumber: (num: string) => void;
  equipList: EquipListType[];
  editMode: boolean;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const { reset, register, setValue } = useFormMethods;
  const { append, fields } = useFieldArrayMethods;
  const registerNewFields = (newEquipment: RowData) => {
    register(`equipment.${fields.length}.fEquipmentAddUrl`);
    setValue(
      `equipment.${fields.length}.fEquipmentAddUrl`,
      newEquipment.fEquipmentAddUrl,
    );
    register(`equipment.${fields.length}.bnNewCompanyItem`);
    setValue(
      `equipment.${fields.length}.bnNewCompanyItem`,
      newEquipment.bnNewCompanyItem,
    );
    register(`equipment.${fields.length}.fEquipmentCompEqFK`);
    setValue(
      `equipment.${fields.length}.fEquipmentCompEqFK`,
      newEquipment.fEquipmentCompEqFK,
    );
  };
  return (
    <Box sx={{ flexGrow: 1 }}>
      {editMode ? (
        <>
          <LegacyMuiButton
            onClick={() => {
              setEditMode((prevValue) => !prevValue);
              setEditedRows([]);
              setDeletedRows([]);
              reset({
                equipment: defaultData,
              });
            }}
            startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Close} />}
            sx={sxStyles.button}
          >
            {t('laCancel')}
          </LegacyMuiButton>
          <LegacyMuiButton
            onClick={() => onSubmit()}
            startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Save} />}
            sx={sxStyles.button}
          >
            {t('laSave')}
          </LegacyMuiButton>
          <Tooltip
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -13],
                  },
                },
              ],
            }}
            sx={{
              tooltip: sxStyles.tooltip,
            }}
            placement="bottom-end"
            open={open}
            TransitionComponent={Fade}
            title={
              <>
                <Box
                  sx={sxStyles.addDiv}
                  onClick={async () => {
                    const newEquipment = newEquipmentData(
                      true,
                      equipList,
                      invNumber,
                    );
                    append(newEquipment);
                    registerNewFields(newEquipment);
                    updateInvNumber(invNumber);
                    setOpen(false);
                  }}
                >
                  <Box component="span" sx={sxStyles.buttonIcon}>
                    <LegacyFAIcon FAIcon={LegacyFAIcons.Plus} />
                  </Box>
                  <Box component="span" sx={sxStyles.buttonText}>
                    {t('laNewCompanyEquipment')}
                  </Box>
                </Box>
                <Box
                  sx={sxStyles.addDiv}
                  onClick={() => {
                    const newEquipment = newEquipmentData(false, equipList);
                    append(newEquipment);
                    registerNewFields(newEquipment);
                    setOpen(false);
                  }}
                >
                  <Box component="span" sx={sxStyles.buttonIcon}>
                    <LegacyFAIcon FAIcon={LegacyFAIcons.Plus} />
                  </Box>
                  <Box component="span" sx={sxStyles.buttonText}>
                    {t('laAddFromCompanyEquipment')}
                  </Box>
                </Box>
              </>
            }
          >
            <LegacyMuiButton
              onClick={() => setOpen(!open)}
              startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Plus} />}
              endIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Down} />}
              sx={sxStyles.button}
            >
              {t('laAdd')}
            </LegacyMuiButton>
          </Tooltip>
        </>
      ) : (
        <LegacyMuiButton
          onClick={() => setEditMode((prevValue) => !prevValue)}
          startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Edit} />}
          sx={sxStyles.button}
        >
          {t('laEdit')}
        </LegacyMuiButton>
      )}
    </Box>
  );
};
